import React, { useState, useMemo } from 'react';
import {
  Box, 
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { 
  GridColDef, 
  GridInitialState, 
  GridRenderCellParams, 
  GridColType,
} from '@mui/x-data-grid-premium';
import GaneshDataGrid from '../DataGrid/GaneshDataGrid';
import useApi from '../../hooks/useGaneshApi';
import { useQuery } from '@tanstack/react-query';
import { useError } from '../../contexts/ErrorContext';
import { DeliveryHealthCheckAggregates_T, DeliveryHealthCheckDetail_T } from '../../types/Delivery';
import { deliveryHealthCheckRenderers, deliveryHealthCheckToFieldMap } from '../../utils/healthCheckRenderers';
import { Link } from 'react-router-dom';
import { formatNumber } from '../../utils/customFormat';
import { getDateValue } from '../../utils/dataGridUtils';
import DeliveryHealthCheckSummaryCards from './DeliveryHealthCheckSummaryCards';

const DeliveriesHealthChecks: React.FC = () => {
  const { apiRequest } = useApi();
  const { showError } = useError();
  
  // State for filtering the delivery issues table
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [activeOnly, setActiveOnly] = useState<boolean>(true); // Default to active only

  // Fetch health check data (includes aggregates, deliveries with issues, and suspicious inactive)
  const fetchHealthCheckData = async () => {
    const response = await apiRequest<{
      aggregates: DeliveryHealthCheckAggregates_T;
      deliveries: DeliveryHealthCheckDetail_T[];
      suspicious_inactive_deliveries: DeliveryHealthCheckDetail_T[];
    }>('/v1/deliveries/health', { method: 'GET' });
    return response.data;
  };

  // Health check data query
  const { 
    data: healthCheckData, 
    isLoading: healthChecksLoading, 
    isError: healthChecksError 
  } = useQuery({
    queryKey: ['delivery-health'],
    queryFn: fetchHealthCheckData,
    staleTime: 120000, // 2 minutes
    refetchInterval: false,
  });

  // Show error message if query failed
  if (healthChecksError) {
    showError('Failed to load delivery health checks');
  }

  // Extract aggregates, deliveries, and suspicious inactive from response
  const aggregates = healthCheckData?.aggregates;
  const allDeliveriesWithIssues = healthCheckData?.deliveries || [];
  const suspiciousInactiveDeliveries = healthCheckData?.suspicious_inactive_deliveries || [];

  // Filter deliveries by active status for summary card calculations
  const deliveriesForSummary = useMemo(() => {
    if (!allDeliveriesWithIssues.length) return [];
    return activeOnly 
      ? allDeliveriesWithIssues.filter(d => d.is_active)
      : allDeliveriesWithIssues;
  }, [allDeliveriesWithIssues, activeOnly]);

  // Recalculate summary statistics based on filtered deliveries
  const dynamicSummaryStats = useMemo(() => {
    if (!aggregates) {
      return null;
    }

    const totalActive = activeOnly
      ? aggregates.total_active_deliveries
      : aggregates.total_deliveries;

    if (!deliveriesForSummary.length && !suspiciousInactiveDeliveries.length) {
      return {
        total_deliveries: aggregates.total_deliveries,
        total_active_deliveries: totalActive,
        total_deliveries_with_issues: 0,
        total_active_deliveries_with_issues: 0,
        // Category counts
        total_deliveries_with_delivery_timeline_issues: 0,
        total_deliveries_with_license_status_issues: 0,
        total_deliveries_with_volume_upgrades_issues: 0,
        total_deliveries_with_fulfillment_setup_issues: 0,
        total_deliveries_with_suspicious_inactive_issues: 0,
        // Suspicious inactive
        total_suspicious_inactive_deliveries: suspiciousInactiveDeliveries.length,
      };
    }

    // Category counts
    const withDeliveryTimeline = deliveriesForSummary.filter(d => d.has_delivery_timeline_issues).length;
    const withLicenseStatus = deliveriesForSummary.filter(d => d.has_license_status_issues).length;
    const withVolumeUpgrades = deliveriesForSummary.filter(d => d.has_volume_upgrades_issues).length;
    const withFulfillmentSetup = deliveriesForSummary.filter(d => d.has_fulfillment_setup_issues).length;
    const withSuspiciousInactive = deliveriesForSummary.filter(d => d.has_suspicious_inactive_issues).length;
    
    const activeIssuesCount = deliveriesForSummary.filter(d => d.is_active).length;

    return {
      total_deliveries: aggregates.total_deliveries,
      total_active_deliveries: totalActive,
      total_deliveries_with_issues: deliveriesForSummary.length,
      total_active_deliveries_with_issues: activeIssuesCount,
      // Category counts
      total_deliveries_with_delivery_timeline_issues: withDeliveryTimeline,
      total_deliveries_with_license_status_issues: withLicenseStatus,
      total_deliveries_with_volume_upgrades_issues: withVolumeUpgrades,
      total_deliveries_with_fulfillment_setup_issues: withFulfillmentSetup,
      total_deliveries_with_suspicious_inactive_issues: withSuspiciousInactive,
      // Suspicious inactive - always show full count
      total_suspicious_inactive_deliveries: suspiciousInactiveDeliveries.length,
    };
  }, [deliveriesForSummary, aggregates, activeOnly, suspiciousInactiveDeliveries]);

  // Handle card clicks to change filter
  const handleCardFilter = (filterType: string) => {
    setSelectedFilter(filterType);
  };

  // Handle toggle of active-only switch
  const handleToggleActiveOnly = () => {
    setActiveOnly(!activeOnly);
  };

  // Filter deliveries based on selected filter (client-side filtering)
  const deliveryIssuesData = useMemo(() => {
    // Special case: Show suspicious inactive deliveries (always show all, ignore activeOnly)
    if (selectedFilter === 'suspicious_inactive') {
      // Ensure all deliveries have valid IDs
      return suspiciousInactiveDeliveries.filter(d => d && d.id != null);
    }

    if (!allDeliveriesWithIssues.length) return [];
    
    // First filter by active status if toggle is on
    let filteredData = activeOnly 
      ? allDeliveriesWithIssues.filter(d => d.is_active)
      : allDeliveriesWithIssues;
    
    // Then apply the selected filter (all/category)
    switch (selectedFilter) {
      case 'all':
        return filteredData;
      // Category filters
      case 'delivery_timeline':
        return filteredData.filter(d => d.has_delivery_timeline_issues);
      case 'license_status':
        return filteredData.filter(d => d.has_license_status_issues);
      case 'volume_upgrades':
        return filteredData.filter(d => d.has_volume_upgrades_issues);
      case 'fulfillment_setup':
        return filteredData.filter(d => d.has_fulfillment_setup_issues);
      default:
        return filteredData;
    }
  }, [allDeliveriesWithIssues, suspiciousInactiveDeliveries, selectedFilter, activeOnly]);

  // Calculate aggregation row data (summary of issues per field)
  // Without showing aggregation for "suspicious inactive" (not needed for this case)
  const aggregationRow = useMemo(() => {
    if (!deliveryIssuesData?.length || selectedFilter === 'suspicious_inactive') return null;
    
    // Count how many deliveries have issues in each field
    const fieldIssueCounts: Record<string, Set<number>> = {};
    
    deliveryIssuesData.forEach(delivery => {
      const healthIssues = delivery.health_issues || [];
      
      healthIssues.forEach((checkName: string) => {
        const field = deliveryHealthCheckToFieldMap[checkName];
        if (field) {
          if (!fieldIssueCounts[field]) {
            fieldIssueCounts[field] = new Set();
          }
          fieldIssueCounts[field].add(delivery.id);
        }
      });
    });
    
    return {
      id: 'AGGREGATION_ROW', // ID for pinned summary row
      delivery_name: `TOTAL (${deliveryIssuesData.length} deliveries)`,
      customer_name: '',
      is_active: fieldIssueCounts['is_active']?.size || 0,
      fulfillment_destination: fieldIssueCounts['fulfillment_destination']?.size || 0,
      fulfillment_groups: fieldIssueCounts['fulfillment_groups']?.size || 0,
      first_delivery_date: fieldIssueCounts['first_delivery_date']?.size || 0,
      most_recent_delivery_date: fieldIssueCounts['most_recent_delivery_date']?.size || 0,
      next_delivery_date: fieldIssueCounts['next_delivery_date']?.size || 0,
      final_scheduled_delivery: fieldIssueCounts['final_scheduled_delivery']?.size || 0,
      license_end_date: fieldIssueCounts['license_end_date']?.size || 0,
      next_volume_upgrade: fieldIssueCounts['next_volume_upgrade']?.size || 0,
      final_scheduled_volume_upgrade: fieldIssueCounts['final_scheduled_volume_upgrade']?.size || 0,
      count_completed_fulfillments: fieldIssueCounts['count_completed_fulfillments']?.size || 0,
      count_fulfillments_on_schedule: fieldIssueCounts['count_fulfillments_on_schedule']?.size || 0,
      count_volume_upgrades_on_schedule: fieldIssueCounts['count_volume_upgrades_on_schedule']?.size || 0,
    };
  }, [deliveryIssuesData, selectedFilter]);

  const getFilterTitle = () => {
    const activePrefix = activeOnly ? 'Active ' : '';
    switch (selectedFilter) {
      case 'all': 
        return `All ${activePrefix}Deliveries with Issues`;
      case 'delivery_timeline': 
        return `${activePrefix}Deliveries with Delivery Timeline Issues`;
      case 'license_status': 
        return `${activePrefix}Deliveries with License & Status Issues`;
      case 'volume_upgrades': 
        return `${activePrefix}Deliveries with Volume Upgrade Issues`;
      case 'fulfillment_setup': 
        return `${activePrefix}Deliveries with Fulfillment Setup Issues`;
      case 'suspicious_inactive': 
        return 'Suspicious Inactive Deliveries';
      default: 
        return `${activePrefix}Deliveries with Issues`;
    }
  };

  // Helper to render aggregation row values
  const renderAggregationValue = (value: number) => (
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: value > 0 ? 'error.main' : 'text.primary' }}>
      {formatNumber(value)}
    </Typography>
  );

  // Delivery issues table columns
  const deliveryIssuesColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') {
          return <Typography variant="body2" sx={{ fontWeight: 'bold' }}>--</Typography>;
        }
        return <Link to={`/delivery/${params.value}`}>{params.value}</Link>;
      }
    },
    {
      field: 'delivery_name',
      headerName: 'Delivery Name',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') {
          return <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{params.value}</Typography>;
        }
        return deliveryHealthCheckRenderers.stringWithMissingCheck(params);
      },
    },
    {
      field: 'customer_name',
      headerName: 'Customer',
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') {
          return <Typography variant="body2" sx={{ fontWeight: 'bold' }}>--</Typography>;
        }
        const customerId = params.row.customer_id;
        if (customerId) {
          return <Link to={`/customer/${customerId}`}>{params.value}</Link>;
        }
        return params.value;
      }
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 80,
      description: 'Current active status of the delivery',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.booleanWithIssueCheck(params);
      },
    },
    {
      field: 'fulfillment_destination',
      headerName: 'Destination',
      width: 120,
      description: 'Fulfillment destination location for this delivery',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.stringWithMissingCheck(params);
      },
    },
    {
      field: 'fulfillment_groups',
      headerName: 'FG',
      width: 110,
      description: 'Fulfillment groups assigned to this delivery',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.stringWithMissingCheck(params);
      },
    },
    {
      field: 'first_delivery_date',
      headerName: 'First Delivery',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Date of the first completed delivery',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.dateWithMissingCheck(params);
      },
    },
    {
      field: 'most_recent_delivery_date',
      headerName: 'Last Delivery',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Date of the most recently completed delivery',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.dateWithMissingCheck(params);
      },
    },
    {
      field: 'next_delivery_date',
      headerName: 'Next Delivery',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Date of the next scheduled delivery',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.dateWithMissingCheck(params);
      },
    },
    {
      field: 'final_scheduled_delivery',
      headerName: 'Final Delivery',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Date of the final scheduled delivery in the series',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.dateWithMissingCheck(params);
      },
    },
    {
      field: 'license_end_date',
      headerName: 'License Ends',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Expiration date of the delivery license',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.dateWithMissingCheck(params);
      },
    },
    {
      field: 'next_volume_upgrade',
      headerName: 'Next Upgrade',
      width: 130,
      type: 'date' as GridColType,
      valueGetter: getDateValue,
      description: 'Date of the next scheduled volume upgrade',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.dateWithMissingCheck(params);
      },
    },
    {
      field: 'count_completed_fulfillments',
      headerName: 'Completed',
      width: 100,
      type: 'number',
      description: 'Number of completed fulfillments',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.numberWithMissingCheck(params);
      },
    },
    {
      field: 'count_fulfillments_on_schedule',
      headerName: 'Scheduled',
      width: 100,
      type: 'number',
      description: 'Number of scheduled fulfillments',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.numberWithMissingCheck(params);
      },
    },
    {
      field: 'count_volume_upgrades_on_schedule',
      headerName: 'Upgrades',
      width: 100,
      type: 'number',
      description: 'Number of scheduled volume upgrades',
      renderCell: (params: GridRenderCellParams) => {
        if (params.row.id === 'AGGREGATION_ROW') return renderAggregationValue(params.value as number);
        return deliveryHealthCheckRenderers.numberWithMissingCheck(params);
      },
    },
  ];

  const deliveryIssuesGridState: GridInitialState = {
    sorting: { sortModel: [{ field: 'id', sort: 'asc' }] },
    pagination: { paginationModel: { pageSize: 25, page: 0 } },
    density: "compact",
  };
  
  return (
    <Box sx={{ px: 1, py: 0 }}>
      {/* Active Only Toggle */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <FormGroup row>
          <FormControlLabel
            control={
              <Switch
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Active deliveries only"
          />
        </FormGroup>
      </Box>

      {/* 1. Summary Cards */}
      <DeliveryHealthCheckSummaryCards 
        loading={healthChecksLoading}
        onCardClick={handleCardFilter}
        selectedFilter={selectedFilter}
        dynamicStats={dynamicSummaryStats}
        activeOnly={activeOnly}
        onToggleActiveOnly={handleToggleActiveOnly}
      />
      
      {/* 2. Delivery Issues Table with Summary Row */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {getFilterTitle()}
        </Typography>
        
        <GaneshDataGrid
          key={`delivery-grid-${selectedFilter}`}
          gridId="deliveryIssuesTable"
          data={deliveryIssuesData || []}
          columns={deliveryIssuesColumns}
          loading={healthChecksLoading}
          defaultGridState={deliveryIssuesGridState}
          pinnedRows={aggregationRow ? { bottom: [aggregationRow] } : undefined}
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            },
            // Style the pinned bottom row (aggregation)
            "& .MuiDataGrid-pinnedRows--bottom": {
              backgroundColor: 'action.hover',
              borderTop: '2px solid',
              borderTopColor: 'divider',
            },
            "& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-cell": {
              fontWeight: 'bold',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default DeliveriesHealthChecks;
